import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import { useFormik } from "formik";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import api from "../../../config/URL";
import * as Yup from "yup";
import fetchAllCentersWithIds from "../../List/CenterList";
import MultiSelect from "react-select";
import CustomDatePicker from "../../CustomDatePicker";

const validationSchema = Yup.object().shape({
  employee: Yup.string().required("*Employee is required"),
  userContractAddress: Yup.string().required("*Address is required"),
  startDateOfEmployment: Yup.string().required(
    "*StartDate Of Employment is required"
  ),
  userContractStartDate: Yup.string().required(
    "*Start Date Of Contract is required"
  ),
  workingDays: Yup.array()
    .min(1, "*Working days are required")
    .required("*Working days are required"),
  userContractSalary: Yup.number()
    .typeError("*Salary Must be numbers")
    .required("*Salary is required"),
  allowance: Yup.number().typeError("*Allowance Must be numbers").notRequired(),
});

const StaffContractAdd = forwardRef(
  ({ formData, setLoadIndicators, setFormData, handleNext }, ref) => {
    console.log("ContractformData", formData);
    const [centerData, setCenterData] = useState([]);
    const [selectedCenters, setSelectedCenters] = useState([]);
    const userName = localStorage.getItem("userName");
    const filteredCenters = centerData?.filter((center) =>
      formData.centerIds.includes(center.id)
    );
    const filteredCenterOptions = filteredCenters.map((center) => ({
      label: center.centerNames,
      value: center.id,
    }));

    const navigate = useNavigate();
    const formik = useFormik({
      initialValues: {
        employer: formData.employer || "",
        employee: formData.teacherName || "",
        uen: formData.uen || "",
        addressOfEmployment: formData.addressOfEmployment || "",
        detailsEmployee: formData.detailsEmployee || "",
        nric: formData.nric || "",
        userContractAddress: formData.address || "",
        jobTitle: formData.jobTitle || "",
        mainDuties: formData.mainDuties || "",
        startDateOfEmployment: formData.startDate || "",
        training: formData.training || "",
        allowance: formData.allowance || "",
        userContractStartDate: formData.startDate || "",
        contactPeriod: formData.contactPeriod || "",
        probation: formData.probation || "",
        workingDays: formData.workingDays || "",
        userContractSalary: formData.salary || "",
        salaryStartDate: formData.effectiveDate || "",
        userContractEndDate: formData.endDate || "",
        payNow: formData.payNow || "",
        internetBanking: formData.internetBanking || "",
        contractDate: formData.contractDate || "",
        terminationNotice: formData.terminationNotice || "",
        createdBy: userName,
      },
      validationSchema: validationSchema,
      onSubmit: async (values) => {
        setLoadIndicators(true);
        values.createdBy = userName;
        // const payload = {
        //   ...values,
        //   employer: Array.isArray(values.employer)
        //     ? values.employer.join(",")
        //     : values.employer,
        // };
        try {
          const response = await api.post(
            `/createUserContractCreation/${formData.user_id}`,
            values,
            {
              headers: {
                "Content-Type": "application/json",
              },
            }
          );
          if (response.status === 201) {
            toast.success("User Contract Created Successfully");
            setFormData((prv) => ({ ...prv, ...values }));
            navigate("/staff");
          } else {
            toast.error(response.data.message);
          }
        } catch (error) {
          toast.error(error);
        } finally {
          setLoadIndicators(false);
        }
      },
      validateOnChange: false, // Enable validation on change
      validateOnBlur: true, // Enable validation on blur
    });

    // Function to scroll to the first error field
    const scrollToError = (errors) => {
      const errorField = Object.keys(errors)[0]; // Get the first error field
      const errorElement = document.querySelector(`[name="${errorField}"]`); // Find the DOM element
      if (errorElement) {
        errorElement.scrollIntoView({ behavior: "smooth", block: "center" });
        errorElement.focus(); // Set focus to the error element
      }
    };

    // Watch for form submit and validation errors
    useEffect(() => {
      if (formik.submitCount > 0 && Object.keys(formik.errors).length > 0) {
        scrollToError(formik.errors);
      }
    }, [formik.submitCount, formik.errors]);

    useImperativeHandle(ref, () => ({
      staffContractAdd: formik.handleSubmit,
    }));

    const fetchData = async () => {
      try {
        const centerData = await fetchAllCentersWithIds();
        setCenterData(centerData);
      } catch (error) {
        toast.error(error);
      }
    };

    const getData = async (centerIds) => {
      try {
        if (centerIds && centerIds.length > 0) {
          const response = await api.get(
            `/getConcatenatedCenters?centerIds=${centerIds.join(",")}`
          );
          formik.setFieldValue("uen", response.data.uen);
          formik.setFieldValue(
            "addressOfEmployment",
            response.data.addressOfEmployment
          );
        } else {
          formik.setFieldValue("uen", "");
          formik.setFieldValue("addressOfEmployment", "");
        }
      } catch (error) {
        toast.error("Error Fetching Center Info");
      }
    };

    useEffect(() => {
      window.scrollTo({ top: 0, left: 0, behavior: "instant" });
      fetchData();

      if (formData.centerIds && formData.centerIds.length > 0) {
        formik.setFieldValue("employer", formData.centerIds);
        getData(formData.centerIds);
      }
    }, []);

    const calculateContractPeriod = (startDate, endDate) => {
      if (!startDate || !endDate) {
        formik.setFieldValue("contactPeriod", "");
        return;
      }

      const start = new Date(startDate);
      const end = new Date(endDate);

      // Validate end date is after start date
      if (end < start) {
        formik.setFieldError(
          "userContractEndDate",
          "End date must be after start date"
        );
        formik.setFieldValue("contactPeriod", "");
        return;
      } else {
        formik.setFieldError("userContractEndDate", undefined);
      }

      let years = end.getFullYear() - start.getFullYear();
      let months = end.getMonth() - start.getMonth();
      let days = end.getDate() - start.getDate();

      if (days < 0) {
        months -= 1;
        days += new Date(
          start.getFullYear(),
          start.getMonth() + 1,
          0
        ).getDate();
      }

      if (months < 0) {
        years -= 1;
        months += 12;
      }

      const contractPeriod = `${years} years, ${months} months, ${days} days`;
      formik.setFieldValue("contactPeriod", contractPeriod);
    };

    // Update contract period when either date changes
    useEffect(() => {
      calculateContractPeriod(
        formik.values.userContractStartDate,
        formik.values.userContractEndDate
      );
    }, [
      formik.values.userContractStartDate,
      formik.values.userContractEndDate,
    ]);

    return (
      <form
        onSubmit={formik.handleSubmit}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !formik.isSubmitting) {
            e.preventDefault(); // Prevent default form submission
          }
        }}
      >
        <div className="container-fluid">
          <p className="headColor my-4">Contract Information</p>
          <div className="container-fluid mt-5" style={{ minHeight: "95vh" }}>
            <span className="mt-3 fw-bold">Details of EMPLOYER</span>
            <div className="row mt-4">
              <div className="col-md-6 col-12 mb-2 mt-3">
                <label>
                  Employer<span className="text-danger">*</span>
                </label>
                <MultiSelect
                  options={filteredCenterOptions}
                  value={filteredCenterOptions.filter((option) =>
                    formik.values.employer.includes(option.value)
                  )}
                  onChange={(selected) => {
                    const selectedIds = selected.map((option) => option.value);
                    setSelectedCenters(selected);
                    formik.setFieldValue("employer", selectedIds);
                    getData(selectedIds); // Fetch and update UEN and Address
                  }}
                  onBlur={() => formik.setFieldTouched("employer", true)}
                  isMulti
                  labelledBy="Select Employers"
                  menuPlacement="top"
                  className={`form-multi-select ${
                    formik.touched.employer && formik.errors.employer
                      ? "is-invalid"
                      : ""
                  }`}
                />

                {formik.touched.employer && formik.errors.employer && (
                  <div className="invalid-feedback">
                    {formik.errors.employer}
                  </div>
                )}
              </div>
              <div className="col-md-6 col-12 mb-2 mt-3">
                <label>UEN</label>
                <input
                  type="text"
                  className="form-control"
                  name="uen"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.uen}
                  readOnly
                />
              </div>
              <div className="col-md-12 col-12 mb-2 mt-3 ">
                <label>Address of Employment</label>
                <textarea
                  type="text"
                  rows={5}
                  className="form-control"
                  name="addressOfEmployment"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.addressOfEmployment}
                  readOnly
                />
              </div>
            </div>
            <div className="row mt-3 ">
              <span className="mt-3 fw-bold ">Details of EMPLOYEE</span>
              <div className="col-md-6 col-12 mb-2 mt-3">
                <label>Employee</label>
                <span className="text-danger">*</span>
                <input
                  type="text"
                  className="form-control"
                  name="employee"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.employee}
                  readOnly
                />
                {formik.touched.employee && formik.errors.employee && (
                  <div className="error text-danger ">
                    <small>{formik.errors.employee}</small>
                  </div>
                )}
              </div>
              <div className="col-md-6 col-12 mb-2 mt-3">
                <label>NRIC</label>
                <input
                  type="text"
                  className="form-control"
                  name="nric"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.nric}
                />
                {formik.touched.nric && formik.errors.nric && (
                  <div className="error text-danger ">
                    <small>{formik.errors.nric}</small>
                  </div>
                )}
              </div>
              <div className="col-md-6 col-12 mb-2 mt-3">
                <label>Address</label>
                <span className="text-danger">*</span>
                <input
                  type="text"
                  className="form-control"
                  name="userContractAddress"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.userContractAddress}
                  readOnly
                />
                {formik.touched.userContractAddress &&
                  formik.errors.userContractAddress && (
                    <div className="error text-danger ">
                      <small>{formik.errors.userContractAddress}</small>
                    </div>
                  )}
              </div>
              <div className="col-md-6 col-12 mb-2 mt-3">
                <label>Job Title</label>
                <input
                  type="text"
                  className="form-control"
                  name="jobTitle"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.jobTitle}
                />
                {formik.touched.jobTitle && formik.errors.jobTitle && (
                  <div className="error text-danger ">
                    <small>{formik.errors.jobTitle}</small>
                  </div>
                )}
              </div>
              <div className="col-md-6 col-12 mb-2 mt-3">
                <label>Main Duties</label>
                <input
                  type="text"
                  className="form-control"
                  name="mainDuties"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.mainDuties}
                />
                {formik.touched.mainDuties && formik.errors.mainDuties && (
                  <div className="error text-danger ">
                    <small>{formik.errors.mainDuties}</small>
                  </div>
                )}
              </div>
              <div className="col-md-6 col-12 mb-2 mt-3">
                <label>Start Date of Employment</label>
                <span className="text-danger">*</span>
                <input
                  type="date"
                  className="form-control"
                  name="startDateOfEmployment"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.startDateOfEmployment}
                  min={new Date().toISOString().split("T")[0]}
                  readOnly
                />
                {formik.touched.startDateOfEmployment &&
                  formik.errors.startDateOfEmployment && (
                    <div className="error text-danger">
                      <small>{formik.errors.startDateOfEmployment}</small>
                    </div>
                  )}
              </div>
              <div className="col-md-6 col-12 mb-2 mt-3">
                <label>Training</label>
                <input
                  type="text"
                  className="form-control"
                  name="training"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.training}
                />
                {formik.touched.training && formik.errors.training && (
                  <div className="error text-danger ">
                    <small>{formik.errors.training}</small>
                  </div>
                )}
              </div>
              <div className="col-md-6 col-12 mb-2 mt-3">
                <label>Allowance</label>
                <input
                  type="text"
                  className="form-control"
                  name="allowance"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.allowance}
                />
                {formik.touched.allowance && formik.errors.allowance && (
                  <div className="error text-danger ">
                    <small>{formik.errors.allowance}</small>
                  </div>
                )}
              </div>
              <div className="col-md-6 col-12 mb-2 mt-3">
                <label>Contract Start Date</label>
                <span className="text-danger">*</span>
                <CustomDatePicker
                  name="userContractStartDate"
                  value={formik.values.userContractStartDate}
                  onChange={(date) => {
                    formik.setFieldValue("userContractStartDate", date);
                    // Also update contract date if needed
                    if (!formik.values.contractDate) {
                      formik.setFieldValue("contractDate", date);
                    }
                  }}
                  onBlur={formik.handleBlur}
                  error={formik.errors.userContractStartDate}
                  touched={formik.touched.userContractStartDate}
                  readOnly={true} // Make it read-only as per requirement
                />
                {formik.touched.userContractStartDate &&
                  formik.errors.userContractStartDate && (
                    <div className="error text-danger">
                      <small>{formik.errors.userContractStartDate}</small>
                    </div>
                  )}
              </div>
              <div className="col-md-6 col-12 mb-2 mt-3">
                <label>Contract End Date</label>
                <CustomDatePicker
                  name="userContractEndDate"
                  value={formik.values.userContractEndDate}
                  onChange={(date) => {
                    formik.setFieldValue("userContractEndDate", date);
                    // Validate end date is after start date
                    if (
                      formik.values.userContractStartDate &&
                      date < new Date(formik.values.userContractStartDate)
                    ) {
                      formik.setFieldError(
                        "userContractEndDate",
                        "End date must be after start date"
                      );
                    } else {
                      formik.setFieldError("userContractEndDate", undefined);
                    }
                  }}
                  onBlur={formik.handleBlur}
                  error={formik.errors.userContractEndDate}
                  touched={formik.touched.userContractEndDate}
                  minDate={
                    formik.values.userContractStartDate
                      ? new Date(formik.values.userContractStartDate)
                      : null
                  }
                />
                {formik.touched.userContractEndDate &&
                  formik.errors.userContractEndDate && (
                    <div className="error text-danger">
                      <small>{formik.errors.userContractEndDate}</small>
                    </div>
                  )}
              </div>
              <div className="col-md-6 col-12 mb-2 mt-3">
                <label>Contract Period</label>
                <input
                  type="text"
                  className="form-control"
                  name="contactPeriod"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.contactPeriod}
                  readOnly
                />
                {formik.touched.contactPeriod &&
                  formik.errors.contactPeriod && (
                    <div className="error text-danger">
                      <small>{formik.errors.contactPeriod}</small>
                    </div>
                  )}
              </div>
              <div className="col-md-6 col-12 mb-2 mt-3">
                <label>Probation (Day)</label>
                <input
                  type="text"
                  className="form-control"
                  name="probation"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.probation}
                />
              </div>

              <div className="col-md-6 col-12 mb-2 mt-3">
                <label>
                  Working Days<span className="text-danger">*</span>
                </label>
                <div className="mt-2 d-flex justify-content-between mt-3">
                  <div className="checkbox-container">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="myCheckbox1"
                      value="MONDAY"
                      name="workingDays"
                      checked={
                        formik.values.workingDays &&
                        formik.values.workingDays.includes("MONDAY")
                      }
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      disabled
                    />
                    <label for="myCheckbox1" className="custom-checkbox">
                      <div className="inner-square"></div>
                    </label>
                    <label for="myCheckbox1" className="mx-1">
                      Mon
                    </label>
                  </div>
                  <div className="checkbox-container">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="myCheckbox2"
                      value="TUESDAY"
                      name="workingDays"
                      checked={
                        formik.values.workingDays &&
                        formik.values.workingDays.includes("TUESDAY")
                      }
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      disabled
                    />
                    <label for="myCheckbox2" className="custom-checkbox">
                      <div className="inner-square"></div>
                    </label>
                    <label for="myCheckbox2" className="mx-1">
                      Tue
                    </label>
                  </div>
                  <div className="checkbox-container">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="myCheckbox3"
                      value="WEDNESDAY"
                      name="workingDays"
                      checked={
                        formik.values.workingDays &&
                        formik.values.workingDays.includes("WEDNESDAY")
                      }
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      disabled
                    />
                    <label for="myCheckbox3" className="custom-checkbox">
                      <div className="inner-square"></div>
                    </label>
                    <label for="myCheckbox3" className="mx-1">
                      Wed
                    </label>
                  </div>
                  <div className="checkbox-container">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="myCheckbox4"
                      value="THURSDAY"
                      name="workingDays"
                      checked={
                        formik.values.workingDays &&
                        formik.values.workingDays.includes("THURSDAY")
                      }
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      disabled
                    />
                    <label for="myCheckbox4" className="custom-checkbox">
                      <div className="inner-square"></div>
                    </label>
                    <label for="myCheckbox4" className="mx-1">
                      Thu
                    </label>
                  </div>
                  <div className="checkbox-container">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="myCheckbox5"
                      value="FRIDAY"
                      name="workingDays"
                      checked={
                        formik.values.workingDays &&
                        formik.values.workingDays.includes("FRIDAY")
                      }
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      disabled
                    />
                    <label for="myCheckbox5" className="custom-checkbox">
                      <div className="inner-square"></div>
                    </label>
                    <label for="myCheckbox5" className="mx-1">
                      Fri
                    </label>
                  </div>
                  <div className="checkbox-container">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="myCheckbox6"
                      value="SATURDAY"
                      name="workingDays"
                      checked={
                        formik.values.workingDays &&
                        formik.values.workingDays.includes("SATURDAY")
                      }
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      disabled
                    />
                    <label for="myCheckbox6" className="custom-checkbox">
                      <div className="inner-square"></div>
                    </label>
                    <label for="myCheckbox6" className="mx-1">
                      Sat
                    </label>
                  </div>
                  <div className="checkbox-container">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="myCheckbox7"
                      value="SUNDAY"
                      name="workingDays"
                      checked={
                        formik.values.workingDays &&
                        formik.values.workingDays.includes("SUNDAY")
                      }
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      disabled
                    />
                    <label for="myCheckbox7" className="custom-checkbox">
                      <div className="inner-square"></div>
                    </label>
                    <label for="myCheckbox7" className="mx-1">
                      Sun
                    </label>
                  </div>
                </div>
                {formik.touched.workingDays && formik.errors.workingDays && (
                  <div className="error text-danger ">
                    <small>{formik.errors.workingDays}</small>
                  </div>
                )}
              </div>

              <div className="col-md-6 col-12 mb-2 mt-3">
                <label>Salary</label>
                <span className="text-danger">*</span>
                <input
                  type="text"
                  className="form-control"
                  name="userContractSalary"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  readOnly
                  value={formik.values.userContractSalary}
                />
                {formik.touched.userContractSalary &&
                  formik.errors.userContractSalary && (
                    <div className="error text-danger ">
                      <small>{formik.errors.userContractSalary}</small>
                    </div>
                  )}
              </div>
              <div className="col-md-6 col-12 mb-2 mt-3">
                <label>Salary Start Date</label>
                <input
                  type="date"
                  className="form-control"
                  name="salaryStartDate"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.salaryStartDate}
                  min={new Date().toISOString().split("T")[0]}
                />
                {formik.touched.salaryStartDate &&
                  formik.errors.salaryStartDate && (
                    <div className="error text-danger ">
                      <small>{formik.errors.salaryStartDate}</small>
                    </div>
                  )}
              </div>
              <div className="row mt-3">
                <span className="mt-3 fw-bold">Bank Account Details</span>
                <div className="col-md-6 col-12 mb-2 mt-3">
                  <label>Pay Now</label>
                  <input
                    type="text"
                    className="form-control"
                    name="payNow"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.payNow}
                  />
                </div>
                <div className="col-md-6 col-12 mb-2 mt-3">
                  <label>Internet Banking</label>
                  <input
                    type="text"
                    className="form-control"
                    name="internetBanking"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.internetBanking}
                  />
                </div>
                <div className="col-md-6 col-12 mb-2 mt-3">
                  <label>Contract Date</label>
                  <input
                    type="date"
                    className="form-control"
                    name="contractDate"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.contractDate}
                  />
                  {formik.touched.contractDate &&
                    formik.errors.contractDate && (
                      <div className="error text-danger ">
                        <small>{formik.errors.contractDate}</small>
                      </div>
                    )}
                </div>
                <div className="col-md-6 col-12 mb-2 mt-3">
                  <label>Termination Notice (Month)</label>
                  <input
                    type="text"
                    className="form-control"
                    name="terminationNotice"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.terminationNotice}
                  />
                  {formik.touched.terminationNotice &&
                    formik.errors.terminationNotice && (
                      <div className="error text-danger ">
                        <small>{formik.errors.terminationNotice}</small>
                      </div>
                    )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </form>
    );
  }
);

export default StaffContractAdd;
